import { faEnvelope, faEnvelopeBadge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { useResendMagicLinkEmailMutation, useUpdateMutation } from 'API/order/signingAppointment/SigningAppointmentAPI';
import { Show } from 'components/shared/Show';
import React from 'react';
import { useParams } from 'react-router-dom';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { SubHeaderTextTW } from 'tailwindComponents/subHeaderText/SubHeaderTextTW';
import { UUID } from 'types/shared/UUID';
import { useSelectAppointmentFromPathParam } from 'utilities/order/scheduling/useSomeAppointmentFromParam';
import { ThemeProvider } from 'styled-components';
import { Alert, Flex, IconSend, TextButton, Text, ThemeConstellation } from '@zillow/constellation';
import { terminalSigningStatuses } from '../../../../../../types/signingAppointment/eSigningAppointmentStatus';
import { ISigningAppointmentResponse } from '../../../../../../types/signingAppointment/ISigningAppointmentResponse';
import { ErrorBoundaryTW } from '../../../../../../tailwindComponents/errorBoundary/ErrorBoundaryTW';
import { isEmpty, isNil } from 'lodash';

export const DigitalScheduling: React.FC = () => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const isDigitalSchedulingDraft = appointmentFromPath?.isDigitalSchedulingDraft;
  const { orderId } = useParams<{ orderId: UUID }>();

  const [updateSigningAppointment, { isLoading: isUpdateLoading }] = useUpdateMutation();

  const onSendDraft = async () => {
    const updateResponse = await updateSigningAppointment({
      id: appointmentFromPath!!.id,
      orderId,
      data: {
        isDigitalSchedulingDraft: false,
      },
    });

    checkForMutationSuccess(updateResponse, 'sending email confirmation to Signer(s).');
  };

  const [resendMagicLinkEmailMutation, { isLoading: isLoadingResendMutation }] = useResendMagicLinkEmailMutation();

  const onResendEmail = async () => {
    const resendResponse = await resendMagicLinkEmailMutation({
      appointmentId: appointmentFromPath!!.id,
      orderId: orderId,
    });
    checkForMutationSuccess(resendResponse, 'sending reminder email to Signer(s).');
  };

  if (!(appointmentFromPath && appointmentFromPath.isDigitalScheduling)) {
    return null;
  }

  if (
    terminalSigningStatuses.includes(appointmentFromPath.status!!) ||
    appointmentFromPath.isDigitalSchedulingConfirmed
  ) {
    return null;
  }

  if (!isDigitalSchedulingDraft && isPastScheduled(appointmentFromPath)) {
    return null;
  }

  const { recipients = [], sentAt } = appointmentFromPath.dssAccess || {};
  const sentAtDate = sentAt ? new Date(sentAt) : undefined;

  return (
    <ErrorBoundaryTW>
      <section data-test-id="digital-scheduling-draft-send">
        <div className={'mb-4 flex flex-row justify-start'}>
          <SubHeaderTextTW className={'mr-5'}>{`Digital Scheduling`}</SubHeaderTextTW>
        </div>
        <Show
          when={!!isDigitalSchedulingDraft}
          fallback={
            <ThemeProvider theme={ThemeConstellation as any}>
              <Alert
                className={'w-fit'}
                body={
                  <Flex display={'flex'} flexDirection={'column'} gap={'sm'}>
                    <Show when={!!sentAtDate && recipients.length > 0}>
                      <Flex display={'flex'} flexDirection={'row'} gap={'sm'} className={'justify-space-between'}>
                        <Text>
                          Email sent <Text as="strong">{sentAtDate?.toLocaleDateString()}</Text> at{' '}
                          <Text as="strong">
                            {sentAtDate?.toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZoneName: 'short',
                            })}
                          </Text>{' '}
                          to {recipients?.join(', ')}
                        </Text>
                      </Flex>
                    </Show>
                    <TextButton
                      className={'w-fit'}
                      data-testid="resend-email-button"
                      onClick={onResendEmail}
                      disabled={isLoadingResendMutation}
                    >
                      Send reminder email
                    </TextButton>
                  </Flex>
                }
                icon={<IconSend />}
              />
            </ThemeProvider>
          }
        >
          <div className={'flex flex-col justify-center'}>
            <section className={'flex flex-col justify-center gap-2'}>
              <div className={'flex justify-center'}>
                <Show when={!isUpdateLoading}>
                  <FontAwesomeIcon
                    className="h-12 w-12 text-extension-foundation-primary-variant"
                    icon={faEnvelopeBadge}
                  />
                </Show>
                <Show when={isUpdateLoading}>
                  <SpinnerTW />
                </Show>
              </div>
              <div className={'flex justify-center gap-3'}>
                <ButtonTW secondary={true} onClick={onSendDraft}>
                  <span className={'mr-1'}>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  Send Email to Signer
                </ButtonTW>
              </div>
            </section>
          </div>
        </Show>
      </section>
    </ErrorBoundaryTW>
  );
};

/**
 * Returns true if the appointment has a scheduled date in the past, or false
 * otherwise.
 *
 * @param appointment - The appointment to check.
 * @returns True if the appointment has a scheduled date in the past, false
 * otherwise.
 */
const isPastScheduled = (appointment: ISigningAppointmentResponse): boolean => {
  if (appointment.appointmentDate === undefined && appointment.dssAppointmentOptions === undefined) {
    return false;
  }

  if (isNil(appointment.appointmentDate) && isEmpty(appointment.dssAppointmentOptions)) {
    return false;
  }

  // if we don't have a date to compare, use the first date in the dssAppointmentOptions
  const dateToCompare = appointment.appointmentDate ?? appointment.dssAppointmentOptions!![0]?.appointmentDate;
  const currentTime = new Date().toISOString();
  const appointmentDate = new Date(dateToCompare!!).toISOString();
  return appointmentDate < currentTime;
};
